"use client";

import { Trophy, Zap } from "lucide-react";
import { formatDuration, playerKey } from "../_logic/scoring";
import type { LeaderboardEntry } from "../_logic/types";

interface WordleLeaderboardProps {
  entries: LeaderboardEntry[];
  loading: boolean;
  error: boolean;
  playerName: string | null;
}

const PODIUM = ["bg-amber-100 text-amber-800", "bg-slate-200 text-slate-700", "bg-orange-100 text-orange-800"];

/**
 * Today's standings: solvers ranked by points, guesses and time, then everyone who didn't solve.
 * @param props Entries from `/api/wordle/leaderboard`, load state, and the current player's name to highlight.
 * @returns The leaderboard panel.
 */
export function WordleLeaderboard({ entries, loading, error, playerName }: WordleLeaderboardProps) {
  // Derived values
  const me = playerName ? playerKey(playerName) : null;
  const solvers = entries.filter((e) => e.won);
  const others = entries.filter((e) => !e.won);

  // Render
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          <h2 className="text-base font-semibold text-slate-900">Today&apos;s leaderboard</h2>
        </div>
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
          {entries.length} {entries.length === 1 ? "player" : "players"}
        </span>
      </header>

      {loading && <LeaderboardSkeleton />}
      {!loading && error && entries.length === 0 && (
        <p className="text-sm font-medium text-slate-500">
          The leaderboard is taking a breather. It&apos;ll refresh shortly.
        </p>
      )}
      {!loading && !error && entries.length === 0 && (
        <p className="text-sm font-medium text-slate-500">
          No one&apos;s solved it yet. First 5 solvers earn +2 bonus points.
        </p>
      )}

      {solvers.length > 0 && (
        <div className="flex flex-col">
          <div className="grid grid-cols-[2rem_1fr_3rem_3.5rem_3rem] gap-2 px-2 pb-2 text-xs font-medium text-slate-500">
            <span>#</span>
            <span>Player</span>
            <span className="text-right">Tries</span>
            <span className="text-right">Time</span>
            <span className="text-right">Pts</span>
          </div>
          <ol className="flex flex-col gap-1">
            {solvers.map((entry) => (
              <LeaderboardRow key={`${entry.name}-${entry.rank}`} entry={entry} isMe={playerKey(entry.name) === me} />
            ))}
          </ol>
        </div>
      )}

      {others.length > 0 && (
        <div className="flex flex-col gap-1 border-t border-slate-100 pt-3">
          <p className="px-2 text-xs font-medium text-slate-500">Didn&apos;t crack it today</p>
          <ul className="flex flex-col gap-1">
            {others.map((entry) => (
              <li
                key={entry.name}
                className={`flex justify-between rounded-lg px-2 py-1.5 text-sm font-medium ${
                  playerKey(entry.name) === me ? "bg-indigo-50 text-indigo-900" : "text-slate-500"
                }`}
              >
                <span className="truncate">{entry.name}</span>
                <span>X/6</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function LeaderboardRow({ entry, isMe }: { entry: LeaderboardEntry; isMe: boolean }) {
  const rank = entry.rank ?? 0;
  return (
    <li
      className={`grid grid-cols-[2rem_1fr_3rem_3.5rem_3rem] items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium ${
        isMe ? "bg-indigo-50 ring-1 ring-indigo-200" : "hover:bg-slate-50"
      }`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
          PODIUM[rank - 1] ?? "text-slate-500"
        }`}
      >
        {rank}
      </span>
      <span className="flex min-w-0 items-center gap-1.5 text-slate-900">
        <span className="truncate">{entry.name}</span>
        {isMe && (
          <span className="shrink-0 rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">You</span>
        )}
        {entry.earlySolver && <Zap className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="First 5 solver" />}
      </span>
      <span className="text-right tabular-nums text-slate-600">{entry.tries}/6</span>
      <span className="text-right tabular-nums text-slate-600">{formatDuration(entry.timeSeconds)}</span>
      <span className="text-right font-semibold tabular-nums text-slate-900">{entry.points}</span>
    </li>
  );
}

function LeaderboardSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
      ))}
    </div>
  );
}
